import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { NOTE_CHANNELS, CUSTOMER_SOURCES, CUSTOMER_STATUSES, formatPrice } from "@/lib/constants";
import { toast } from "sonner";
import { Phone, Mail, Plus, Pencil, Printer } from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [interests, setInterests] = useState([]);
  const [notes, setNotes] = useState([]);
  const [offers, setOffers] = useState([]);
  const [showNoteForm, setShowNoteForm] = useState(searchParams.get("note") === "1");
  const [users, setUsers] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [assignTo, setAssignTo] = useState("");
  const [note, setNote] = useState({ channel: "Telefon", result: "", note: "", next_followup_at: "" });
  const [editForm, setEditForm] = useState(null);
  const [expandedLists, setExpandedLists] = useState({});
  const [expandedOfferId, setExpandedOfferId] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${id}`);
      setCustomer(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);

  useEffect(() => {
    load();
    api.get(`/customers/${id}/interests`).then(({ data }) => setInterests(data));
    api.get(`/customers/${id}/notes`).then(({ data }) => setNotes(data));
    api.get(`/customers/${id}/offers`).then(({ data }) => setOffers(data)).catch(() => setOffers([]));
    api.get("/company/users").then(({ data }) => setUsers(data));
  }, [id, load]);

  const submitNote = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notes", { ...note, customer_id: id });
      toast.success("Not eklendi");
      setShowNoteForm(false);
      setNote({ channel: "Telefon", result: "", note: "", next_followup_at: "" });
      api.get(`/customers/${id}/notes`).then(({ data }) => setNotes(data));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/customers/${id}/assign?agent_id=${assignTo}`);
      toast.success("Müşteri atandı");
      setShowAssign(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = () => {
    setEditForm({
      full_name: customer.full_name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      source: customer.source || "Telefon",
      status: customer.status || "Yeni",
      budget_min: customer.budget_min || 0,
      budget_max: customer.budget_max || 0,
      assigned_agent_id: customer.assigned_agent_id || "",
      next_followup_at: (customer.next_followup_at || "").slice(0, 10),
      general_note: customer.general_note || "",
    });
    setShowEdit(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editForm,
        budget_min: Number(editForm.budget_min) || 0,
        budget_max: Number(editForm.budget_max) || 0,
      };
      // Agent cannot reassign
      if (user?.role === "agent") delete payload.assigned_agent_id;
      else if (!payload.assigned_agent_id) delete payload.assigned_agent_id;
      if (!payload.next_followup_at) delete payload.next_followup_at;
      await api.put(`/customers/${id}`, payload);
      toast.success("Müşteri güncellendi");
      setShowEdit(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!customer) return <div className="text-slate-500">Yükleniyor…</div>;

  const canEdit = user?.role === "admin" || user?.role === "manager" ||
                  (user?.role === "agent" && customer.assigned_agent_id === user?.id);
  const isListExpanded = (key) => Boolean(expandedLists[key]);
  const toggleList = (key) => setExpandedLists((current) => ({ ...current, [key]: !current[key] }));
  const visibleRecords = (key, records) => (isListExpanded(key) ? records : records.slice(0, 2));
  const recordListClass = (key, records) => (
    isListExpanded(key) && records.length > 5 ? "divide-y divide-slate-100 max-h-[360px] overflow-y-auto" : "divide-y divide-slate-100"
  );

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-display" data-testid="customer-name">{customer.full_name}</h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-slate-600 flex-wrap">
            {customer.phone && <span className="inline-flex items-center gap-1"><Phone className="w-4 h-4" /> {customer.phone}</span>}
            {customer.email && <span className="inline-flex items-center gap-1"><Mail className="w-4 h-4" /> {customer.email}</span>}
            <span>•</span>
            <span className="status-chip status-Satışta"><span className="status-dot" />{customer.status}</span>
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {canEdit && (
            <button onClick={openEdit}
                    className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:border-slate-300 inline-flex items-center gap-2"
                    data-testid="edit-customer-btn">
              <Pencil className="w-4 h-4" /> Müşteriyi Düzenle
            </button>
          )}
          {user?.role !== "agent" && (
            <button onClick={() => setShowAssign(true)}
                    className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:border-slate-300"
                    data-testid="assign-btn">
              Personel Ata
            </button>
          )}
          <button onClick={() => setShowNoteForm(true)}
                  className="px-3 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg inline-flex items-center gap-2"
                  data-testid="add-note-btn">
            <Plus className="w-4 h-4" /> Görüşme Notu Ekle
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Box l="Kaynak" v={customer.source} />
        <Box l="Atanan" v={customer.assigned_agent_name || "—"} />
        <Box l="Bütçe" v={`${formatPrice(customer.budget_min, customer.currency)} - ${formatPrice(customer.budget_max, customer.currency)}`} />
        <Box l="Sonraki Takip" v={(customer.next_followup_at || "").slice(0, 10) || "—"} />
      </div>

      {customer.general_note && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Genel Not</div>
          <div className="text-sm text-slate-700">{customer.general_note}</div>
        </div>
      )}

      {!!customer.project_preferences?.length && <section className="border-t border-slate-200 py-4"><h2 className="font-semibold mb-3">İlgilenilen projeler ve daire tercihleri</h2>{customer.project_preferences.map((preference, index) => <div key={index} className="py-2"><Link className="font-medium underline" to={`/projects/${preference.project_id}`}>{preference.project_name}</Link><p className="text-sm text-slate-600">{[preference.room_type, preference.layout_type].filter(Boolean).join(" · ") || "Proje geneli; daire tipi henüz seçilmedi"}</p></div>)}</section>}

      <Section
        title={`İlgilenilen Bölümler (${interests.length})`}
        testid="customer-interests"
        collapsible={interests.length > 2}
        expanded={isListExpanded("interests")}
        hiddenCount={Math.max(interests.length - 2, 0)}
        onToggle={() => toggleList("interests")}
      >
        {interests.length === 0 ? <Empty>Henüz ilgi kaydı yok</Empty> : (
          <ul className={recordListClass("interests", interests)}>
            {visibleRecords("interests", interests).map((i) => (
              <li key={i.id} className="px-4 py-3 text-sm flex justify-between items-start gap-3" data-testid={`interest-row-${i.id}`}>
                <div className="min-w-0">
                  <Link to={`/units/${i.unit_id}`} className="font-medium text-slate-900 hover:text-amber-600">
                    {i.project_name || "Proje bilgisi yok"} / {i.block_name || "Blok bilgisi yok"} / {i.floor_name || "Kat bilgisi yok"} / {i.unit_no || "—"}
                  </Link>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {i.unit_room_type || "—"} • <span className="font-medium">{i.unit_status || "—"}</span>
                    {i.sales_agent_name ? <> • Personel: {i.sales_agent_name}</> : null}
                  </div>
                  {i.note && <div className="text-xs text-slate-500 mt-1 italic">&ldquo;{i.note}&rdquo;</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-slate-500">İlgi: {i.status}</div>
                  <div className="font-semibold text-sm">{formatPrice(i.unit_price, i.currency || "TRY")}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title={`Teklif Geçmişi (${offers.length})`}
        testid="customer-offers"
        collapsible={offers.length > 2}
        expanded={isListExpanded("offers")}
        hiddenCount={Math.max(offers.length - 2, 0)}
        onToggle={() => toggleList("offers")}
      >
        {offers.length === 0 ? <Empty>Henüz teklif kaydı yok</Empty> : (
          <ul className={recordListClass("offers", offers)}>
            {visibleRecords("offers", offers).map((offer) => (
              <li key={offer.id} className="px-4 py-3 text-sm" data-testid={`customer-offer-${offer.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <Link to={`/units/${offer.unit_id}`} className="font-medium text-slate-900 hover:text-amber-600">
                      {offer.project_name || "Proje bilgisi yok"} / {offer.block_name || "Blok bilgisi yok"} / {offer.floor_name || "Kat bilgisi yok"} / {offer.unit_no || "—"}
                    </Link>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {offer.phone || "Telefon yok"} • {offer.sales_agent_name || "Satışçı"} • {offer.channel || "WhatsApp"}
                    </div>
                    {offer.valid_until && <div className="text-xs text-slate-500 mt-1">Geçerlilik: {offer.valid_until}</div>}
                    {offer.message && (
                      <button type="button"
                              onClick={() => setExpandedOfferId((current) => current === offer.id ? "" : offer.id)}
                              className="mt-2 text-xs font-semibold text-amber-700 hover:text-amber-800">
                        {expandedOfferId === offer.id ? "Mesajı gizle" : "Mesajı göster"}
                      </button>
                    )}
                    <Link to={`/offers/${offer.id}/print`} target="_blank"
                          className="mt-2 ml-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-amber-700">
                      <Printer className="w-3 h-3" /> PDF / Yazdır
                    </Link>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-amber-700">
                      {offer.include_price ? formatPrice(offer.offered_price, offer.currency) : "Fiyatsız"}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">{(offer.created_at || "").slice(0, 19).replace("T", " ")}</div>
                  </div>
                </div>
                {expandedOfferId === offer.id && offer.message && (
                  <div className="mt-3 space-y-2">
                    <pre className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700 font-mono">
                      {offer.message}
                    </pre>
                    {offer.conversation_note && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
                        <div className="font-semibold mb-1">Görüşme notu</div>
                        <div>{offer.conversation_note}</div>
                        {offer.next_followup_at && <div className="mt-1 text-amber-700">Takip: {offer.next_followup_at}</div>}
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title={`Görüşme Notları (${notes.length})`}
        collapsible={notes.length > 2}
        expanded={isListExpanded("notes")}
        hiddenCount={Math.max(notes.length - 2, 0)}
        onToggle={() => toggleList("notes")}
      >
        {notes.length === 0 ? <Empty>Henüz not yok</Empty> : (
          <ul className={recordListClass("notes", notes)}>
            {visibleRecords("notes", notes).map((n) => (
              <li key={n.id} className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900">{n.channel} • {n.result || "—"}</div>
                  <div className="text-xs text-slate-400">{(n.created_at || "").slice(0, 10)}</div>
                </div>
                <div className="text-slate-700 mt-1">{n.note}</div>
                {n.next_followup_at && <div className="text-xs text-slate-500 mt-1">Sonraki takip: {n.next_followup_at.slice(0, 10)}</div>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {showNoteForm && (
        <Modal onClose={() => setShowNoteForm(false)} title="Görüşme Notu Ekle">
          <form onSubmit={submitNote} className="space-y-3" data-testid="note-form">
            <Field label="Kanal">
              <select className="input" value={note.channel} onChange={(e) => setNote({ ...note, channel: e.target.value })}>
                {NOTE_CHANNELS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Sonuç"><input className="input" value={note.result} onChange={(e) => setNote({ ...note, result: e.target.value })} /></Field>
            <Field label="Not" required><textarea required rows={3} className="input resize-none" value={note.note} onChange={(e) => setNote({ ...note, note: e.target.value })} data-testid="note-text" /></Field>
            <Field label="Sonraki Takip"><input type="date" className="input" value={note.next_followup_at?.slice(0, 10) || ""} onChange={(e) => setNote({ ...note, next_followup_at: e.target.value })} /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowNoteForm(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
              <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg" data-testid="submit-note">Kaydet</button>
            </div>
          </form>
        </Modal>
      )}

      {showAssign && (
        <Modal onClose={() => setShowAssign(false)} title="Personel Ata">
          <form onSubmit={submitAssign} className="space-y-3" data-testid="assign-form">
            <Field label="Personel" required>
              <select required className="input" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} data-testid="assign-select">
                <option value="">Seçin</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowAssign(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
              <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg" data-testid="submit-assign">Ata</button>
            </div>
          </form>
        </Modal>
      )}

      {showEdit && editForm && (
        <Modal onClose={() => setShowEdit(false)} title="Müşteriyi Düzenle">
          <form onSubmit={submitEdit} className="space-y-3" data-testid="edit-customer-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Ad Soyad" required>
                <input required className="input" value={editForm.full_name}
                       onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                       data-testid="edit-customer-name" />
              </Field>
              <Field label="Telefon">
                <input className="input" value={editForm.phone}
                       onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                       data-testid="edit-customer-phone" />
              </Field>
              <Field label="E-posta">
                <input type="email" className="input" value={editForm.email}
                       onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </Field>
              <Field label="Kaynak">
                <select className="input" value={editForm.source}
                        onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}>
                  {CUSTOMER_SOURCES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Durum">
                <select className="input" value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        data-testid="edit-customer-status">
                  {CUSTOMER_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              {user?.role !== "agent" && (
                <Field label="Atanan Personel">
                  <select className="input" value={editForm.assigned_agent_id}
                          onChange={(e) => setEditForm({ ...editForm, assigned_agent_id: e.target.value })}
                          data-testid="edit-customer-agent">
                    <option value="">—</option>
                    {users.filter((u) => u.role !== "admin").map((u) =>
                      <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Bütçe Min">
                <input type="number" className="input" value={editForm.budget_min}
                       onChange={(e) => setEditForm({ ...editForm, budget_min: e.target.value })} />
              </Field>
              <Field label="Bütçe Max">
                <input type="number" className="input" value={editForm.budget_max}
                       onChange={(e) => setEditForm({ ...editForm, budget_max: e.target.value })} />
              </Field>
              <Field label="Sonraki Takip">
                <input type="date" className="input" value={editForm.next_followup_at}
                       onChange={(e) => setEditForm({ ...editForm, next_followup_at: e.target.value })} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Genel Not">
                  <textarea rows={2} className="input resize-none" value={editForm.general_note}
                            onChange={(e) => setEditForm({ ...editForm, general_note: e.target.value })} />
                </Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowEdit(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
              <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg" data-testid="submit-edit-customer">Kaydet</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Box({ l, v }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</div>
      <div className="mt-1 font-semibold text-slate-900 text-sm">{v}</div>
    </div>
  );
}
function Section({ title, children, testid, collapsible = false, expanded = false, hiddenCount = 0, onToggle }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-slate-700">{title}</div>
        {collapsible && (
          <button type="button"
                  onClick={onToggle}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-amber-700"
                  aria-expanded={expanded}>
            <span>{expanded ? "Kapat" : `${hiddenCount} kayıt daha`}</span>
            <span className="text-base leading-none">{expanded ? "⌃" : "⌄"}</span>
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
function Empty({ children }) {
  return <div className="px-4 py-8 text-sm text-slate-400 text-center">{children}</div>;
}
function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label} {required && <span className="text-red-500">*</span>}</div>
      {children}
    </label>
  );
}
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 !mt-0 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="text-lg font-semibold text-slate-900 mb-4 font-display">{title}</div>
        {children}
      </div>
    </div>
  );
}
